import { useQuery } from '@apollo/client';
import { Container, FilterChips } from '@components/Filter';
import { FilterButton } from '@components/Filter/FilterButton';
import { FilterDrawer } from '@components/Filter/FilterDrawer';
import { hasValue } from '@components/Filter/filter.utils';
import { FlexRow } from '@components/FlexRow';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import InputDateRange from '@components/InputDateRange';
import InputAutocomplete from '@components/InputAutocomplete';
import AutoCompletePro from '@components/AutoComplete/AutoCompletePro';
import { GET_USER_SUBSCRIPTION_PLANS_FILTER_DATASET_UPERCUT } from '@gql/subscriptions';
import { Box, FormGroup, FormLabel } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
    defaultSubscriptionPlansFilters,
    SubscriptionPlansFilterForm,
    subscriptionPlansFilterLabels,
} from './types';

interface SubscriptionPlansFilterProps {
    filter: SubscriptionPlansFilterForm;
    onFilterChange: (filter: SubscriptionPlansFilterForm) => void;
    totalDocs: number;
    fixedProId?: string;
    query: string;
    onQueryChange: (query: string) => void;
}

interface FilterDatasetResponse {
    getUserSubscriptionPlansFilterDatasetUpercut: {
        statuses: string[];
        partnerNames: string[];
    };
}

const buildDateRange = (range?: { start?: Date | string | null; end?: Date | string | null }) => {
    const result: Record<string, string> = {};

    if (range?.start) {
        result.start = range.start instanceof Date ? range.start.toISOString() : String(range.start);
    }

    if (range?.end) {
        result.end = range.end instanceof Date ? range.end.toISOString() : String(range.end);
    }

    return Object.keys(result).length ? result : undefined;
};

const buildDatasetFilter = (filter: SubscriptionPlansFilterForm, fixedProId?: string) => {
    const result: Record<string, unknown> = {};

    if (fixedProId || filter.proId) {
        result.proId = fixedProId || filter.proId;
    }

    if (filter.status) {
        result.status = [filter.status];
    }

    if (filter.partnerName) {
        result.partnerName = filter.partnerName;
    }

    const createdAt = buildDateRange(filter.createdAt);
    if (createdAt) {
        result.createdAt = createdAt;
    }

    const commitmentStartDate = buildDateRange(filter.commitmentStartDate);
    if (commitmentStartDate) {
        result.commitmentStartDate = commitmentStartDate;
    }

    const commitmentEndDate = buildDateRange(filter.commitmentEndDate);
    if (commitmentEndDate) {
        result.commitmentEndDate = commitmentEndDate;
    }

    return Object.keys(result).length ? result : undefined;
};

const SubscriptionPlansFilter = ({
    filter,
    onFilterChange,
    totalDocs,
    fixedProId,
    query,
    onQueryChange,
}: SubscriptionPlansFilterProps) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [proSelectorKey, setProSelectorKey] = useState(0);

    const methods = useForm<SubscriptionPlansFilterForm>({
        defaultValues: filter,
    });

    useEffect(() => {
        methods.reset(filter);
    }, [filter, methods]);

    const datasetFilter = useMemo(() => buildDatasetFilter(filter, fixedProId), [filter, fixedProId]);

    const { data } = useQuery<FilterDatasetResponse>(GET_USER_SUBSCRIPTION_PLANS_FILTER_DATASET_UPERCUT, {
        variables: {
            query,
            filter: datasetFilter,
        },
    });

    const statusOptions = useMemo(
        () =>
            (data?.getUserSubscriptionPlansFilterDatasetUpercut?.statuses ?? []).map(status => ({
                label: status,
                value: status,
            })),
        [data],
    );

    const partnerOptions = useMemo(
        () =>
            (data?.getUserSubscriptionPlansFilterDatasetUpercut?.partnerNames ?? []).map(partnerName => ({
                label: partnerName,
                value: partnerName,
            })),
        [data],
    );

    const toggleDrawer = (open: boolean) => () => {
        setIsDrawerOpen(open);
    };

    const onApplyFilter = (data: SubscriptionPlansFilterForm) => {
        const normalizedFilter = {
            ...data,
            proId: fixedProId || data.proId || '',
        };

        onFilterChange(normalizedFilter);
        setIsDrawerOpen(false);
    };

    const onResetFilter = () => {
        const resetFilter = {
            ...defaultSubscriptionPlansFilters,
            proId: fixedProId || '',
        };

        methods.reset(resetFilter);
        onFilterChange(resetFilter);
        setIsDrawerOpen(false);
        setProSelectorKey(prev => prev + 1);
    };

    const hasActiveFilters = useMemo(() => {
        const filterToInspect = fixedProId ? { ...filter, proId: '' } : filter;
        return Object.values(filterToInspect).some(value => hasValue(value));
    }, [filter, fixedProId]);

    return (
        <FormProvider {...methods}>
            <Container>
                <FlexRow sx={{ justifyContent: 'space-between', width: '100%' }}>
                    <FlexRow sx={{ gap: 2 }}>
                        <SearchBarWithNumber
                            placeholder="Recherche par email ou nom commercial"
                            totalCount={totalDocs}
                            onChange={onQueryChange}
                        />
                        <FilterButton toggleDrawer={toggleDrawer} hasActiveFilters={hasActiveFilters} />
                    </FlexRow>
                </FlexRow>
            </Container>

            {hasActiveFilters && (
                <Container sx={{ mt: 2, mb: 1 }}>
                    <FlexRow>
                        <FilterChips
                            filter={fixedProId ? { ...filter, proId: '' } : filter}
                            labels={subscriptionPlansFilterLabels}
                            defaultFilters={defaultSubscriptionPlansFilters}
                            onFilterChange={nextFilter => {
                                const normalizedFilter = {
                                    ...nextFilter,
                                    proId: fixedProId || nextFilter.proId || '',
                                };
                                onFilterChange(normalizedFilter);
                            }}
                        />
                    </FlexRow>
                </Container>
            )}

            <FilterDrawer
                isDrawerOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                onApplyFilter={onApplyFilter}
                onResetFilter={onResetFilter}
            >
                {!fixedProId && (
                    <FormGroup>
                        <FormLabel>{subscriptionPlansFilterLabels.proId}</FormLabel>
                        <Box key={proSelectorKey}>
                            <AutoCompletePro
                                size="small"
                                onChange={proId => {
                                    methods.setValue('proId', proId || '', {
                                        shouldDirty: true,
                                        shouldTouch: true,
                                        shouldValidate: true,
                                    });
                                }}
                            />
                        </Box>
                    </FormGroup>
                )}

                <FormGroup>
                    <FormLabel>{subscriptionPlansFilterLabels.status}</FormLabel>
                    <InputAutocomplete name="status" options={statusOptions} />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{subscriptionPlansFilterLabels.partnerName}</FormLabel>
                    <InputAutocomplete name="partnerName" options={partnerOptions} />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{subscriptionPlansFilterLabels.createdAt}</FormLabel>
                    <InputDateRange name="createdAt" />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{subscriptionPlansFilterLabels.commitmentStartDate}</FormLabel>
                    <InputDateRange name="commitmentStartDate" />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{subscriptionPlansFilterLabels.commitmentEndDate}</FormLabel>
                    <InputDateRange name="commitmentEndDate" />
                </FormGroup>
            </FilterDrawer>
        </FormProvider>
    );
};

export default SubscriptionPlansFilter;
